<template>
  <aside class="sidebar">
    <div class="sidebar-logo">
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <rect width="36" height="36" rx="10" fill="#6366f1"/>
        <path d="M10 14h16M10 18h10M10 22h13" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
      <span>DevTask</span>
    </div>

    <div class="sidebar-user">
      <div class="user-avatar">{{ initials }}</div>
      <div class="user-info">
        <div class="user-name">{{ user?.name }}</div>
        <div class="user-role">{{ roleLabel }}</div>
      </div>
    </div>

    <nav class="sidebar-nav">
      <router-link to="/" exact-active-class="active" class="nav-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
        Dashboard
      </router-link>

      <!-- My Tasks: for internal users this is "mine=1"; for external it's their linked tasks -->
      <router-link
        :to="isExternal ? '/tasks' : '/tasks?mine=1'"
        class="nav-item"
        :class="{ active: $route.path === '/tasks' && (isExternal || $route.query.mine) }"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
        My Tasks
      </router-link>

      <!-- Team Tasks: internal only -->
      <router-link v-if="!isExternal" to="/tasks" class="nav-item" :class="{ active: $route.path === '/tasks' && !$route.query.mine }">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        Team Tasks
      </router-link>

      <!-- Projects: all users (external sees shared only) -->
      <router-link to="/projects" active-class="active" class="nav-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
        Projects
      </router-link>

      <!-- Archive: internal only -->
      <router-link v-if="!isExternal" to="/archive" active-class="active" class="nav-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        Archive
      </router-link>

      <!-- Clients: manager only -->
      <router-link v-if="isManager" to="/clients" active-class="active" class="nav-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg>
        Clients
      </router-link>
    </nav>

    <div class="sidebar-bottom">
      <button class="nav-item logout-btn" @click="$emit('logout')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        Sign out
      </button>
    </div>
  </aside>
</template>

<script>
import { computed } from 'vue'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'AppSidebar',
  emits: ['logout'],
  setup() {
    const { currentUser, isManager: _isManager, isExternalClient } = useAuth()

    const user      = computed(() => currentUser())
    const isExternal = computed(() => isExternalClient())
    const isManager  = computed(() => _isManager())
    const roleLabel  = computed(() => {
      if (isExternal.value) return 'Client'
      return user.value?.role === 'manager' ? 'Manager' : 'Developer'
    })
    const initials = computed(() => {
      const name = user.value?.name || ''
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
    })

    return { user, isExternal, isManager, roleLabel, initials }
  },
}
</script>

<style scoped>
.sidebar {
  width: 220px;
  min-height: 100vh;
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  flex-shrink: 0;
}
.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 24px;
  color: var(--text);
}
.sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--border);
}
.user-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.user-name { font-size: 13px; font-weight: 600; line-height: 1.2; }
.user-role { font-size: 11px; color: var(--muted); margin-top: 1px; }

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 10px;
  flex: 1;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 7px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background .15s, color .15s;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}
.nav-item:hover  { background: rgba(255,255,255,.05); color: var(--text); }
.nav-item.active { background: rgba(99,102,241,.15); color: var(--primary); }
.nav-item svg { flex-shrink: 0; }

.sidebar-bottom {
  padding: 12px 10px 0;
  border-top: 1px solid var(--border);
  margin-top: 8px;
}
.logout-btn { color: var(--muted); }
.logout-btn:hover { color: var(--danger); background: rgba(239,68,68,.1); }
</style>
